import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { TrendingDown, Users, Calendar, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';

export const NewRatesSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  // NEW Pricing Data (Updated)
  const memberRates = {
    standard: [
      { sessions: '1x/week', perSession: '$42.30', termPrice: '$423', discount: '-' },
      { sessions: '2x/week', perSession: '$37.65', termPrice: '$753', discount: '11%' },
      { sessions: '3x/week', perSession: '$32.00', termPrice: '$960', discount: '24%' },
    ],
    sibling2: [
      { sessions: '1x/week', perSession: '$33.84', termPrice: '$338.40', discount: '20%' },
      { sessions: '2x/week', perSession: '$30.12', termPrice: '$602.40', discount: '29%' },
      { sessions: '3x/week', perSession: '$25.60', termPrice: '$768', discount: '39%' },
    ],
    sibling3: [
      { sessions: '1x/week', perSession: '$31.73', termPrice: '$317.30', discount: '25%' },
      { sessions: '2x/week', perSession: '$28.24', termPrice: '$564.80', discount: '33%' },
      { sessions: '3x/week', perSession: '$24.00', termPrice: '$720', discount: '43%' },
    ],
    adhoc: {
      standalone: '$50'
    }
  };

  const nonMemberRates = {
    standard: [
      { sessions: '1x/week', perSession: '$47.00', termPrice: '$470', discount: '-' },
      { sessions: '2x/week', perSession: '$41.83', termPrice: '$836.60', discount: '11%' },
      { sessions: '3x/week', perSession: '$35.56', termPrice: '$1,066.80', discount: '24%' },
    ],
    sibling2: [
      { sessions: '1x/week', perSession: '$37.60', termPrice: '$376', discount: '20%' },
      { sessions: '2x/week', perSession: '$33.46', termPrice: '$669.20', discount: '29%' },
      { sessions: '3x/week', perSession: '$28.45', termPrice: '$853.50', discount: '39%' },
    ],
    sibling3: [
      { sessions: '1x/week', perSession: '$35.25', termPrice: '$352.50', discount: '25%' },
      { sessions: '2x/week', perSession: '$31.37', termPrice: '$627.40', discount: '33%' },
      { sessions: '3x/week', perSession: '$26.67', termPrice: '$800.10', discount: '43%' },
    ],
    adhoc: {
      standalone: '$55'
    }
  };

  const RatesTable = ({ rates, title, subtitle, isPerChild = false }) => (
    <Card className="bg-gradient-card gold-border overflow-hidden">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          {title.includes('Sibling') && <Users className="h-5 w-5 text-primary" />}
          <CardTitle className="font-display text-lg text-card-foreground">
            {title}
          </CardTitle>
        </div>
        <CardDescription className="text-muted-foreground">
          {subtitle}
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-4">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-muted-foreground">Sessions</TableHead>
              <TableHead className="text-muted-foreground text-right">Per Session</TableHead>
              <TableHead className="text-muted-foreground text-right">
                {isPerChild ? 'Term Price (per child)' : 'Term Price'}
              </TableHead>
              <TableHead className="text-muted-foreground text-right">Discount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rates.map((rate, idx) => (
              <TableRow key={idx} className={`border-border ${idx === 2 ? 'bg-primary/10' : ''}`}>
                <TableCell className="font-medium text-card-foreground">{rate.sessions}</TableCell>
                <TableCell className="text-right text-card-foreground">{rate.perSession}</TableCell>
                <TableCell className={`text-right font-bold ${idx === 2 ? 'text-primary' : 'text-card-foreground'}`}>
                  {rate.termPrice}
                </TableCell>
                <TableCell className="text-right">
                  {rate.discount !== '-' ? (
                    <Badge className="bg-success/20 text-success border-0">{rate.discount}</Badge>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );

  const AdHocCard = ({ adhocRate, memberType }) => (
    <Card className="bg-gradient-card gold-border overflow-hidden">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-primary" />
          <CardTitle className="font-display text-lg text-card-foreground">
            Ad-Hoc Sessions
          </CardTitle>
        </div>
        <CardDescription className="text-muted-foreground">
          Can&apos;t commit to a full term? No problem.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-4">
        <div className="glass-card rounded-lg p-4 border border-border text-center mb-4">
          <p className="text-muted-foreground text-sm mb-2">Per Session</p>
          <p className="text-3xl font-display font-bold text-primary">{adhocRate}</p>
        </div>
        <div className="bg-charcoal rounded-lg p-4 border border-border">
          <p className="text-primary font-medium text-sm mb-2">Already on a term package?</p>
          <p className="text-muted-foreground text-xs">
            Add extra sessions at {adhocRate}/session when you can't commit to upgrading your tier. 
            Perfect for occasional extra training without a full commitment.
          </p>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <section id="new-rates" className="min-h-screen py-24 px-6">
      <div className="max-w-6xl mx-auto">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="text-center mb-12">
            <Badge variant="outline" className="gold-border text-primary mb-4">
              <Calendar className="h-3 w-3 mr-1" />
              2026 Term 1
            </Badge>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-gradient-gold mb-4">
              Academy Rates
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-base">
              Fair, transparent pricing with progressive discounts for committed players
            </p>
            <p className="text-primary text-sm mt-2 font-medium">
              Each session is 1.5 hours
            </p>
          </motion.div>

          {/* Key Feature */}
          <motion.div variants={itemVariants} className="mb-8">
            <Card className="bg-success/10 border-success/30">
              <CardContent className="p-4 flex items-start gap-4">
                <TrendingDown className="h-6 w-6 text-success flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-success mb-1">Descending Fees with Increased Participation</h4>
                  <p className="text-muted-foreground text-sm">
                    The more you commit, the less you pay per session. Multi-session packages and sibling enrollments offer significant savings up to 43% off.
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Pricing Tabs */}
          <motion.div variants={itemVariants}>
            <Tabs defaultValue="member" className="w-full">
              <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-8 bg-charcoal">
                <TabsTrigger value="member" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                  Member
                </TabsTrigger>
                <TabsTrigger value="nonMember" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                  Non-Member
                </TabsTrigger>
              </TabsList>

              {/* Member Tab */}
              <TabsContent value="member">
                <div className="space-y-8">
                  <RatesTable 
                    rates={memberRates.standard} 
                    title="Standard Rates" 
                    subtitle="Individual child term package"
                  />
                  <RatesTable 
                    rates={memberRates.sibling2} 
                    title="Sibling Bundle (2 Children)" 
                    subtitle="Per child pricing when 2 children are enrolled together"
                    isPerChild={true}
                  />
                  <RatesTable 
                    rates={memberRates.sibling3} 
                    title="Sibling Bundle (3 Children)" 
                    subtitle="Per child pricing when 3 children are enrolled together"
                    isPerChild={true}
                  />
                  <AdHocCard adhocRate={memberRates.adhoc.standalone} memberType="Member" />
                </div>
              </TabsContent>

              {/* Non-Member Tab */}
              <TabsContent value="nonMember">
                <div className="space-y-8">
                  <RatesTable 
                    rates={nonMemberRates.standard} 
                    title="Standard Rates" 
                    subtitle="Individual child term package"
                  />
                  <RatesTable 
                    rates={nonMemberRates.sibling2} 
                    title="Sibling Bundle (2 Children)" 
                    subtitle="Per child pricing when 2 children are enrolled together"
                    isPerChild={true}
                  />
                  <RatesTable 
                    rates={nonMemberRates.sibling3} 
                    title="Sibling Bundle (3 Children)" 
                    subtitle="Per child pricing when 3 children are enrolled together"
                    isPerChild={true}
                  />
                  <AdHocCard adhocRate={nonMemberRates.adhoc.standalone} memberType="Non-Member" />
                </div>
              </TabsContent>
            </Tabs>
          </motion.div>

          {/* Notes */}
          <motion.div variants={itemVariants} className="mt-8">
            <div className="glass-card rounded-lg p-4 gold-border">
              <p className="text-muted-foreground text-sm text-center">
                All rates are for <span className="text-primary font-medium">2026 Term 1</span>. 
                Discounts are calculated vs Tier 1 base rate. Member discount is 5% off Non-Member rates.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default NewRatesSection;
